"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, Clock, FileCheck, Hammer, Layers, Ruler, ShieldAlert, Sparkles, TrendingUp, Users, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const timelineFactors = [{
  step: "01",
  title: "Design finalization",
  duration: "1 to 2 Weeks",
  description: "Detailed 3D layout planning, footprint optimization, stair placement, and seamless indoor-outdoor transitions tailored to your yard's topography.",
  icon: Ruler
}, {
  step: "02",
  title: "Material selection",
  duration: "1 to 2 Weeks",
  description: "Choosing composite, PVC, or natural hardwoods, coupled with railing styles, hidden fastener systems, integrated lighting, and shade structures.",
  icon: Hammer
}, {
  step: "03",
  title: "City of Brookhaven permitting",
  duration: "2 to 4 Weeks",
  description: "Submitting comprehensive architectural drawings and site plans directly through Brookhaven community development to ensure code compliance.",
  icon: FileCheck
}, {
  step: "04",
  title: "Structural inspections (deck replacement)",
  duration: "1 Week / Ongoing",
  description: "Evaluation of ledger boards, existing footings, load-bearing soil conditions, and framing integrity before framing and final sign-offs.",
  icon: Clock
}];
const lifeTriggers = [{
  title: "Aging or unsafe existing deck",
  description: "Wobbly rails, splintering boards, rusted connectors, or rotted support posts that present clear safety liabilities for family and guests.",
  icon: ShieldAlert,
  badge: "Safety Priority"
}, {
  title: "Want more outdoor entertaining space",
  description: "Your existing layout is too cramped for dining, grilling, or hosting Brookhaven gatherings with friends and neighbors.",
  icon: Users,
  badge: "Lifestyle"
}, {
  title: "Material upgrade to low maintenance",
  description: "Tired of annual power washing, staining, and sealing wood; transitioning to composite or PVC decking for decades of resilience.",
  icon: Sparkles,
  badge: "Durability"
}, {
  title: "Preparing to sell your home",
  description: "Modern outdoor living spaces offer one of the strongest curb appeal boosts and highest return-on-investment points in Brookhaven real estate.",
  icon: TrendingUp,
  badge: "High ROI"
}, {
  title: "Desire for a custom multi-level design",
  description: "Maximizing steep slope yards with tiered decks, walk-out basement coverings, under-deck drainage, and dedicated lounge zones.",
  icon: Layers,
  badge: "Architecture"
}];
export default function DeckTimelineAndTriggers() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="decktimelineandtriggers" data-nav="light" ref={sectionRef} className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Part 1: Timeline Factors */}
        <div className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 lg:mb-16 gap-6">
            <div className="max-w-2xl">
              <span className="text-primary font-mono text-sm tracking-wider uppercase font-semibold">
                Project Scheduling
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight mt-2 text-foreground">
                Deck remodeling timeline factors
              </h2>
              <p className="text-muted-foreground mt-4 text-base sm:text-lg leading-relaxed">
                Every custom deck build in Brookhaven follows four critical planning and structural phases before construction wraps smoothly.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button asChild variant="outline" className="border-border">
                <Link to="/ContactRequestAnEstimatePage">
                  Discuss your timeline
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {timelineFactors.map((factor, index) => {
            const Icon = factor.icon;
            return <Card key={factor.step} data-index={index} className="bg-card border-border/70 hover:border-primary/50 transition-colors shadow-sm relative flex flex-col justify-between">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-mono text-xs font-semibold px-2 py-1 rounded bg-muted text-muted-foreground">
                        Phase {factor.step}
                      </span>
                      <div className="p-2 rounded-md bg-accent text-primary">
                        <Icon className="size-5" />
                      </div>
                    </div>
                    <CardTitle className="text-lg font-serif font-medium text-card-foreground">
                      {factor.title}
                    </CardTitle>
                    <div className="flex items-center gap-1.5 text-xs font-mono text-primary font-medium mt-1">
                      <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                      <span>{factor.duration}</span>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {factor.description}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* Visual Break / Showcase Image */}
        <div className="my-16 lg:my-24 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 relative overflow-hidden rounded-xl shadow-md border border-border">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_custom_deck_outdoor_living.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_custom_deck_outdoor_living.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_custom_deck_outdoor_living.png-wvc-sizes" alt="Custom composite deck with multi-level seating and built-in railings in Brookhaven" loading="lazy" decoding="async" className="w-full h-80 sm:h-96 lg:h-[420px] object-cover hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
            <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6 text-white pointer-events-none">
              <span className="text-xs uppercase tracking-widest font-mono bg-primary text-primary-foreground px-2 py-0.5 rounded">
                Brookhaven, GA
              </span>
              <p className="mt-2 text-sm sm:text-base font-medium drop-shadow-sm">
                Engineered for strict local permitting guidelines and enduring structural safety.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col justify-center space-y-4">
            <span className="text-primary font-mono text-xs tracking-wider uppercase font-semibold">
              Permits &amp; Codes
            </span>
            <h3 className="text-2xl sm:text-3xl font-serif font-semibold text-foreground">
              Built strictly to DeKalb County &amp; City of Brookhaven standards
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
              From calculating footing depths down to the frost line to managing setbacks and tree protection ordinances, we handle every permit submission and mandatory municipal inspection so your build stays on track.
            </p>
            <div className="pt-2">
              <Button asChild variant="secondary" className="shadow-xs">
                <Link to="/OurProjectsPage">
                  Browse completed decks
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Part 2: Life-Moment Triggers */}
        <div className={`transition-all duration-700 ease-out delay-150 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="max-w-3xl mb-12">
            <span className="text-primary font-mono text-sm tracking-wider uppercase font-semibold">
              Evaluation Guide
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight mt-2 text-foreground">
              Is it time to build or remodel your deck?
            </h2>
            <p className="text-muted-foreground mt-4 text-base sm:text-lg leading-relaxed">
              Homeowners typically decide to renovate when their outdoor living setup no longer keeps up with their lifestyle or structural integrity begins to slip.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lifeTriggers.map((trigger, i) => {
            const Icon = trigger.icon;
            return <div key={trigger.title} data-index={i} className="bg-card border border-border rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-4">
                      <div className="p-2.5 rounded-lg bg-muted text-primary">
                        <Icon className="size-5" />
                      </div>
                      <Badge variant="outline" className="font-mono text-[11px] border-border/80">
                        {trigger.badge}
                      </Badge>
                    </div>
                    <h3 className="text-lg font-serif font-medium text-card-foreground mb-2">
                      {trigger.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {trigger.description}
                    </p>
                  </div>

                  <div className="pt-6 mt-4 border-t border-border/50 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground font-mono">
                      Trigger #{i + 1}
                    </span>
                    <Link to="/ContactRequestAnEstimatePage" className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1">
                      Assess this
                      <ArrowRight className="size-3" />
                    </Link>
                  </div>
                </div>;
          })}

            {/* Final CTA Card fitting the 6th slot of the 3-col grid */}
            <div className="bg-secondary text-secondary-foreground rounded-xl p-6 shadow-sm flex flex-col justify-between">
              <div>
                <span className="font-mono text-xs uppercase tracking-wider text-primary font-semibold">
                  Get Started
                </span>
                <h3 className="text-xl font-serif font-medium mt-2 mb-3">
                  Ready to assess your Brookhaven property?
                </h3>
                <p className="text-sm text-secondary-foreground/80 leading-relaxed">
                  Book an on-site structural evaluation and comprehensive design consultation with our local carpentry specialists.
                </p>
              </div>
              <div className="pt-6 mt-4">
                <Button asChild variant="default" className="w-full">
                  <Link to="/ContactRequestAnEstimatePage">
                    Request an estimate
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>;
}